import React, { useState, useEffect, useRef } from "react";
import FlashTestForm from "./FlashTestForm";
import { generateFlashTest, getRecruiterExams, getFlashTestAllResults, getFlashTestQuestions, addFlashTestQuestion, updateFlashTestQuestion, deleteFlashTestQuestion, getFlashTestResult, deleteFlashTest, updateFlashTestStatus, updateFlashTestShowAnswers } from "../../../services/flashTestService";
import { useUserContext } from "../../common/UserProvider";
import { fmtDuration, fmtDateTime, summarizeExamResults, getDisplayQuestions } from "./summarizeresults";
import "./FlashTest.css";
import editRecruiterIcon from "../../../images/edit_recruiter.png";
import deleteRecruiterIcon from "../../../images/delete_recruiter.png";
import viewRecruiterIcon from "../../../images/view_recruiter.png";
import linkRecruiterIcon from "../../../images/link_recruiter.png";

const cleanText = (txt) => {
  if (!txt) return "";
  return String(txt)
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

const formatDateTimeImage = (ts) => {
  if (!ts) return "—";
  let d;
  if (Array.isArray(ts)) {
    if (ts.length < 3) return "—";
    const [year, month, day, hour = 0, minute = 0, second = 0] = ts;
    d = new Date(year, month - 1, day, hour, minute, second);
  } else {
    d = new Date(ts);
  }
  if (isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, '0');
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthStr = months[d.getMonth()];
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hourStr = String(hours).padStart(2, '0');

  return `${day} ${monthStr} ${year}, ${hourStr}:${minutes} ${ampm}`;
};

const formatDurationImage = (durationMs) => {
  if (!durationMs) return "—";
  const totalSec = Math.floor(durationMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;

  const hStr = h > 0 ? `${h} hr${h > 1 ? 's' : ''}` : "";
  const mStr = m > 0 ? `${m} min${m > 1 ? 's' : ''}` : "";

  const sStr = s > 0 ? `${s} sec${s > 1 ? 's' : ''}` : "";

  const parts = [];
  if (hStr) parts.push(hStr);
  if (mStr) parts.push(mStr);
  if (sStr) parts.push(sStr);

  return parts.join(" ") || "0 secs";

};

const getScoreColor = (score, passingScore) => {
  if (score === null || score === undefined) return "#64748b";
  if (score >= passingScore) return "#10b981";
  if (score >= 30) return "#f97316";
  return "#ef4444";
};

const StickyHorizontalScrollbar = ({ targetRef }) => {
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);
  const [left, setLeft] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const updateSizes = () => {
      const rect = target.getBoundingClientRect();
      setScrollWidth(target.scrollWidth);
      setClientWidth(target.clientWidth);
      setLeft(rect.left);

      // Show scrollbar only if the content overflows horizontally,
      // and the table viewport is in the screen but the bottom is below the fold.
      const tableInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      const bottomBelowViewport = rect.bottom > window.innerHeight;
      setIsVisible(target.scrollWidth > target.clientWidth && tableInViewport && bottomBelowViewport);

      if (barRef.current) {
        barRef.current.scrollLeft = target.scrollLeft;
      }
    };

    const handleTargetScroll = () => {
      if (barRef.current) {
        barRef.current.scrollLeft = target.scrollLeft;
      }
    };

    target.addEventListener("scroll", handleTargetScroll);
    window.addEventListener("scroll", updateSizes);
    window.addEventListener("resize", updateSizes);

    // Watch for size and position changes using ResizeObserver
    const resizeObserver = new ResizeObserver(updateSizes);
    resizeObserver.observe(target);
    if (target.parentElement) {
      resizeObserver.observe(target.parentElement);
    }

    updateSizes();

    return () => {
      target.removeEventListener("scroll", handleTargetScroll);
      window.removeEventListener("scroll", updateSizes);
      window.removeEventListener("resize", updateSizes);
      resizeObserver.disconnect();
    };
  }, [targetRef]);

  if (!isVisible) return null;

  return (
    <div
      ref={barRef}
      onScroll={() => {
        if (targetRef.current && barRef.current) {
          targetRef.current.scrollLeft = barRef.current.scrollLeft;
        }
      }}
      className="rigid-scrollbar"
      style={{
        position: "fixed",
        bottom: 0,
        left: left,
        width: clientWidth,
        overflowX: "scroll",
        zIndex: 9999,
        background: "grey",
        boxShadow: "none",
        height: "8px"
      }}
    >
      <div style={{ width: scrollWidth, height: "1px" }} />
    </div>
  );
};

const FlashTest = () => {
  const { user } = useUserContext();
  const attemptsTableContainerRef = useRef(null);
  const allTestsTableContainerRef = useRef(null);
  const [activeTab, setActiveTab] = useState("questions"); // "questions" | "settings" | "results"
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateStatus, setGenerateStatus] = useState(""); // loading message or error message
  const [generateError, setGenerateError] = useState(""); // backend error to display inline
  const [createdTestId, setCreatedTestId] = useState(null);
  const [selectedTestIdForResults, setSelectedTestIdForResults] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [testSearchQuery, setTestSearchQuery] = useState("");
  const [selectedAttemptForReview, setSelectedAttemptForReview] = useState(null);
  const [loadingTests, setLoadingTests] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [fetchedAttempts, setFetchedAttempts] = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [reviewQuestions, setReviewQuestions] = useState([]);
  const [resultsQuestions, setResultsQuestions] = useState([]);
  const [cellReviewData, setCellReviewData] = useState(null);

  // View/Preview and CRUD Questions state
  const [viewingTestId, setViewingTestId] = useState(null);
  const [viewingQuestions, setViewingQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null); // holds question object for Add/Edit
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const [hideActionsForTest, setHideActionsForTest] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);

  // Close row dropdown action menu when clicking outside
  useEffect(() => {
    const closeMenu = () => setActiveMenuId(null);
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, []);

  // Settings State
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem("flashTestSettings");
    return saved ? JSON.parse(saved) : {
      passcode: "",
      timeLimit: 0,
      passingScore: 60,
      maxAttempts: 1,
      showScore: true,
      showAnswers: true,
      shuffleQuestions: false,
      shuffleChoices: false,
      oneQuestionPerPage: false
    };
  });

  // Save settings to localStorage on change
  useEffect(() => {
    localStorage.setItem("flashTestSettings", JSON.stringify(settings));
  }, [settings]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Results Tab state
  const [allTests, setAllTests] = useState([]);

  const fetchRecruiterExams = async () => {
    setLoadingTests(true);
    setFetchError("");
    try {
      // const recruiterId = user?.id || localStorage.getItem("recruiterId") || 3;
      const response = await getRecruiterExams();
      const backendExams = response?.data || [];

      const mappedExams = backendExams.map(exam => {
        const localData = JSON.parse(localStorage.getItem(`flashTest_${exam.examId}`) || "null");
        return {
          id: exam.examId,
          dbId: exam.id,
          examName: exam.examName,
          subject: exam.subject,
          category: exam.category,
          difficulty: exam.difficulty,
          questionCount: exam.totalQuestions ?? localData?.questions?.length ?? 5,
          questions: localData?.questions || [],
          duration: exam.duration ?? localData?.duration ?? null,
          averageTime: exam.averageTime ?? exam.avgTime ?? null,
          createdAt: exam.createdAt || exam.createdDate || exam.creationDate || localData?.createdAt || null,
          colorScheme: exam.colorScheme ?? exam.color_scheme ?? 5,
          isActive: exam.isActive ?? true,
          settings: {
            ...(localData?.settings || {
              passcode: "",
              timeLimit: 0,
              passingScore: 60,
              maxAttempts: 1,
              showScore: true,
              shuffleQuestions: false,
              shuffleChoices: false,
              oneQuestionPerPage: false
            }),
            showAnswers: exam.showAnswers ?? localData?.settings?.showAnswers ?? true
          }
        };
      });

      setAllTests(mappedExams);
      localStorage.setItem("allFlashTests", JSON.stringify(mappedExams));
    } catch (err) {
      console.error("Failed to fetch recruiter exams:", err);
      setFetchError("Failed to sync exams from database. Showing offline/local data.");
      const saved = localStorage.getItem("allFlashTests");
      setAllTests(saved ? JSON.parse(saved) : []);
    } finally {
      setLoadingTests(false);
    }
  };

  const handleViewResults = async (examId) => {
    setLoadingAttempts(true);
    setFetchedAttempts([]);
    setSelectedTestIdForResults(examId);
    setSelectedAttemptForReview(null);
    setReviewQuestions([]);
    setResultsQuestions([]);
    try {
      const [resultsRes, questionsRes] = await Promise.all([
        getFlashTestAllResults(examId).catch(err => {
          console.error("Failed to fetch all results:", err);
          return null;
        }),
        getFlashTestQuestions(examId).catch(err => {
          console.error("Failed to fetch questions:", err);
          return null;
        })
      ]);

      let attemptsList = [];
      if (resultsRes && resultsRes.data && Array.isArray(resultsRes.data) && resultsRes.data.length > 0) {
        attemptsList = resultsRes.data;
      } else {
        const attemptsKey = `flashTestAttempts_${examId}`;
        attemptsList = JSON.parse(localStorage.getItem(attemptsKey) || "[]");
      }

      let mappedQuestions = [];
      if (questionsRes && questionsRes.data) {
        const fetchedQuestions = Array.isArray(questionsRes.data) ? questionsRes.data : (questionsRes.data.questions || []);
        mappedQuestions = fetchedQuestions.map(q => {
          const resolvedId = q.id ?? q.questionId ?? q.question_id ?? q.qid ?? q.examQuestionId ?? null;
          return {
            ...q,
            id: resolvedId,
            question: q.question || "",
            code: q.code || "",
            options: q.options || { A: "", B: "", C: "", D: "" },
            correctOption: (q.correctOption || "A").toUpperCase(),
            answerText: q.answerText || ""
          };
        });
        setResultsQuestions(mappedQuestions);
      }

      const enriched = await Promise.all(attemptsList.map(async (att) => {
        let durationMs = att.durationMs;
        if (!durationMs && att.startTime && att.endTime) {
          let startD, endD;
          if (Array.isArray(att.startTime) && att.startTime.length >= 3) {
            const [y, m, d, h = 0, min = 0, s = 0] = att.startTime;
            startD = new Date(y, m - 1, d, h, min, s);
          } else {
            startD = new Date(att.startTime);
          }
          if (Array.isArray(att.endTime) && att.endTime.length >= 3) {
            const [y, m, d, h = 0, min = 0, s = 0] = att.endTime;
            endD = new Date(y, m - 1, d, h, min, s);
          } else {
            endD = new Date(att.endTime);
          }
          if (!isNaN(startD.getTime()) && !isNaN(endD.getTime())) {
            durationMs = endD.getTime() - startD.getTime();
          }
        }

        let questionResults = att.questionResults || [];
        let selectedOptions = att.selectedOptions || {};
        let questionDetails = att.questionDetails || [];

        const applicantId = att.id || att.studentId || att.applicantId || att.applicant_id;
        if (examId && applicantId && (!att.questionDetails || att.questionDetails.length === 0)) {
          try {
            const detailRes = await getFlashTestResult(examId, applicantId);
            if (detailRes && detailRes.data) {
              const details = detailRes.data.questionDetails || [];
              questionDetails = details;

              if (mappedQuestions.length > 0) {
                questionResults = [];
                selectedOptions = {};
                mappedQuestions.forEach((q, idx) => {
                  const qTextClean = cleanText(q.question);
                  const detail = details.find(d => cleanText(d.questionText) === qTextClean);
                  if (detail) {
                    const userOpt = detail.userOption ? detail.userOption.toUpperCase() : null;
                    selectedOptions[idx] = userOpt;
                    const correctOpt = (q.correctOption || "").toUpperCase();
                    questionResults[idx] = !!(userOpt && correctOpt && userOpt === correctOpt);
                  } else {
                    selectedOptions[idx] = null;
                    questionResults[idx] = false;
                  }
                });
              }
            }
          } catch (err) {
            console.error(`Failed to fetch detailed result for candidate ${applicantId}:`, err);
          }
        }

        return {
          ...att,
          durationMs,
          questionResults,
          selectedOptions,
          questionDetails
        };
      }));

      setFetchedAttempts(enriched);
    } catch (err) {
      console.error("Failed to fetch exam attempts from API:", err);
      const attemptsKey = `flashTestAttempts_${examId}`;
      const localAttempts = JSON.parse(localStorage.getItem(attemptsKey) || "[]");
      setFetchedAttempts(localAttempts);
    } finally {
      setLoadingAttempts(false);
    }
  };

  const handleCellClick = (att, q, status, userOption, isCorrect) => {
    setCellReviewData({
      candidateName: att.name || "Anonymous",
      candidateRegno: att.regno || "N/A",
      question: q.question,
      code: q.code,
      options: q.options || {},
      correctOption: q.correctOption,
      answerText: q.answerText || q.explanation || "",
      userOption: userOption,
      status: status,
      isCorrect: isCorrect
    });
  };

  const handleLoadCandidateReview = async (att) => {
    const hasAttempt = att.score !== null && att.score !== undefined;
    if (!hasAttempt) return;
    try {
      const examId = selectedTestIdForResults || att.examId;
      const applicantId = att.id || att.studentId || att.applicantId || att.applicant_id;
      console.log("[FlashTest] Loading candidate review: att object keys:", Object.keys(att), "att:", JSON.stringify(att));

      if (examId && applicantId) {
        const [questionsRes, resultRes] = await Promise.all([
          getFlashTestQuestions(examId).catch(err => {
            console.error("Failed to load questions:", err);
            return null;
          }),
          getFlashTestResult(examId, applicantId)
        ]);

        if (resultRes?.data) {
          const resultData = resultRes.data;
          let updatedAtt = { ...att };

          const questionDetails = resultData.questionDetails || [];
          const fetchedQuestions = Array.isArray(questionsRes?.data) ? questionsRes.data : (questionsRes?.data?.questions || []);

          let fullQuestions = [];
          if (questionDetails.length > 0) {
            fullQuestions = questionDetails.map(detail => {
              const detailQId = detail.questionId;
              const q = fetchedQuestions.find(fq => {
                const resolvedFqId = fq.id ?? fq.questionId ?? fq.question_id ?? fq.qid ?? fq.examQuestionId ?? null;
                return String(resolvedFqId) === String(detailQId);
              });

              let options = {};
              if (q && q.options) {
                if (typeof q.options === "object" && !Array.isArray(q.options)) {
                  options = {};
                  Object.entries(q.options).forEach(([k, v]) => {
                    options[k.toUpperCase()] = v;
                  });
                } else if (Array.isArray(q.options)) {
                  const keys = ["A", "B", "C", "D"];
                  q.options.forEach((val, idx) => {
                    options[keys[idx]] = val;
                  });
                }
              } else {
                if (detail.userOption) {
                  options[detail.userOption.toUpperCase()] = detail.userAnswerText || "";
                }
                if (detail.correctOption) {
                  options[detail.correctOption.toUpperCase()] = detail.correctAnswerText || "";
                }
              }

              return {
                id: detailQId,
                question: q?.question || detail.questionText || "",
                code: q?.code || "",
                options: options,
                correctOption: (q?.correctOption || detail.correctOption || "A").toUpperCase(),
                answerText: q?.answerText || detail.correctAnswerText || ""
              };
            });
          }
          setReviewQuestions(fullQuestions);

          if (fullQuestions.length > 0) {
            const selectedOptions = {};
            const questionResults = [];
            let correctCount = 0;
            let notAnsweredCount = 0;

            fullQuestions.forEach((q, idx) => {
              const qTextClean = cleanText(q.question);
              const detail = questionDetails.find(d => cleanText(d.questionText) === qTextClean);
              if (detail) {
                const userOpt = detail.userOption ? detail.userOption.toUpperCase() : null;
                selectedOptions[idx] = userOpt;
                const correctOpt = (q.correctOption || "").toUpperCase();
                const isCorrect = !!(userOpt && correctOpt && userOpt === correctOpt);
                const isNotAnswered = !userOpt ||
                  userOpt === "" ||
                  userOpt === "NULL" ||
                  userOpt === "NA" ||
                  userOpt === "N/A" ||
                  userOpt === "—" ||
                  userOpt === "-" ||
                  userOpt === "—";
                questionResults[idx] = isCorrect;
                if (isCorrect) {
                  correctCount++;
                } else if (isNotAnswered) {
                  notAnsweredCount++;
                }
              } else {
                selectedOptions[idx] = null;
                questionResults[idx] = false;
                notAnsweredCount++;
              }
            });

            updatedAtt.selectedOptions = selectedOptions;
            updatedAtt.questionResults = questionResults;
            updatedAtt.correctCount = correctCount;
            updatedAtt.notAnsweredCount = notAnsweredCount;
            updatedAtt.wrongCount = fullQuestions.length - correctCount - notAnsweredCount;
          }

          setSelectedAttemptForReview(updatedAtt);
        } else {
          setSelectedAttemptForReview(att);
        }
      } else {
        setSelectedAttemptForReview(att);
      }
    } catch (err) {
      console.error("Failed to fetch review data:", err);
      setSelectedAttemptForReview(att);
    }
  };


  const handleApplySettings = () => {
    localStorage.setItem("flashTestSettings", JSON.stringify(settings));
    const existingTestsJson = localStorage.getItem("allFlashTests");
    const existingTests = existingTestsJson ? JSON.parse(existingTestsJson) : [];

    const updatedTests = existingTests.map(t => {
      const updatedTest = {
        ...t,
        settings: {
          ...(t.settings || {}),
          ...settings
        }
      };
      localStorage.setItem(`flashTest_${t.id}`, JSON.stringify(updatedTest));
      return updatedTest;
    });

    localStorage.setItem("allFlashTests", JSON.stringify(updatedTests));
    setAllTests(updatedTests);
    showToast("Settings applied successfully!");
  };

  useEffect(() => {
    if (activeTab === "results") {
      fetchRecruiterExams();
    } else {
      setSelectedTestIdForResults(null);
      setSelectedAttemptForReview(null);
      setSearchFilter("");
      setFetchedAttempts([]);
      setLoadingAttempts(false);
    }
    if (activeTab !== "questions") {
      setViewingTestId(null);
      setViewingQuestions([]);
      setEditingQuestion(null);
      setHideActionsForTest(false);
    }
  }, [activeTab]);

  // Toast state
  const [toastMessage, setToastMessage] = useState("");

  const showToast = (message) => {
    setToastMessage(message);
  };

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage("");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Helper to format code snippets in questions
  const formatQuestionText = (text) => {
    if (!text) return "";
    let formatted = text;

    const codeBlockRegex = /```(?:[a-zA-Z]+)?\n([\s\S]*?)\n```/g;
    formatted = formatted.replace(codeBlockRegex, (match, codeSnippet) => {
      return `<pre class="code-snippet-box" style="background: #1e1e1e; color: #d4d4d4; padding: 12px; border-radius: 6px; font-family: monospace; text-align: left; overflow-x: auto; margin: 15px 0; border-left: 4px solid #F68318;"><code>${codeSnippet.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</code></pre>`;
    });

    formatted = formatted.replace(/`([^`]+)`/g, '<code style="background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-family: monospace; color: #e11d48;">$1</code>');

    const parts = formatted.split(/(<pre[\s\S]*?<\/pre>)/g);
    for (let i = 0; i < parts.length; i++) {
      if (!parts[i].startsWith("<pre")) {
        parts[i] = parts[i].replace(/\n/g, "<br/>&nbsp;&nbsp;&nbsp;&nbsp;").replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;");
      }
    }
    return parts.join("");
  };


  const handleGenerate = async (formValues) => {
    setIsGenerating(true);
    setGenerateError("");

    const maxAttempts = 3;
    const recruiterId = user?.id || localStorage.getItem("recruiterId") || 3;

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        if (attempt > 1) {
          setGenerateStatus(`Retrying (Attempt ${attempt} of ${maxAttempts})...`);
          // Wait 2.5 seconds before retrying to let server / AI engine recover
          await delay(2500);
        } else {
          setGenerateStatus("Connecting to AI engine...");
        }

        setGenerateStatus(`Generating questions (Attempt ${attempt} of ${maxAttempts}) — this may take up to 60 seconds...`);

        const response = await generateFlashTest(formValues, recruiterId);
        const responseData = response?.data;

        // The API may return:
        // Option A: { id: 3, examId: "uuid", questions: [...], examName: "...", ... }
        // Option B: a plain array [{ id, question, code, options, ... }]
        const isArrayResponse = Array.isArray(responseData);
        const examObj = isArrayResponse ? {} : responseData;

        const questionsList = examObj.questions
          || (isArrayResponse ? responseData : []);

        const examId = examObj.examId || examObj.id || null;

        console.log(`[FlashTest] Create response (Attempt ${attempt}): examId:`, examId, "| questions:", questionsList?.length);

        if ((examId && Array.isArray(questionsList) && questionsList.length > 0) ||
          (!examId && Array.isArray(questionsList) && questionsList.length > 0)) {

          const testId = examId ? examId.toString() : Date.now().toString();
          const generatedData = {
            id: testId,
            examName: formValues.examName || examObj.examName || `${formValues.subject} Test`,
            subject: formValues.subject,
            category: formValues.category,
            difficulty: formValues.difficulty,
            questionCount: formValues.questionCount,
            duration: formValues.duration,
            questions: questionsList,
            settings: settings
          };

          // Save test data to localStorage
          localStorage.setItem(`flashTest_${testId}`, JSON.stringify(generatedData));
          localStorage.setItem("latestFlashTest", JSON.stringify(generatedData));

          // Add to list of all tests
          const existingTestsJson = localStorage.getItem("allFlashTests");
          const existingTests = existingTestsJson ? JSON.parse(existingTestsJson) : [];
          const filteredTests = existingTests.filter(t => t.id.toString() !== testId);
          filteredTests.unshift({ id: testId, ...generatedData });
          localStorage.setItem("allFlashTests", JSON.stringify(filteredTests));

          // Fetch fresh list from backend to update state & local storage
          try {
            await fetchRecruiterExams();
          } catch (err) {
            console.warn("Failed to fetch fresh exams list after generation:", err);
          }

          setGenerateStatus("");
          setCreatedTestId(testId);
          showToast("Exam created successfully.");
          setIsGenerating(false);
          return; // Success! Exit the function.
        } else {
          // Backend responded but returned no usable data or empty list
          const serverMsg = examObj?.message || examObj?.error || "No questions were generated.";
          throw new Error(serverMsg);
        }
      } catch (error) {
        console.warn(`[FlashTest] Generation attempt ${attempt} of ${maxAttempts} failed:`, error);

        const status = error?.response?.status;
        const msg = error?.backendMessage || error?.message || "Failed to generate test. Please try again.";

        // Stop immediately if it's an authentication, authorization, or invalid request payload error
        if (status === 401 || status === 403 || status === 400) {
          setGenerateError(msg);
          setGenerateStatus("");
          setIsGenerating(false);
          return;
        }

        // If it's the last attempt, report the error
        if (attempt === maxAttempts) {
          setGenerateError(`Failed after ${maxAttempts} attempts: ${msg}`);
          setGenerateStatus("");
        }
      }
    }

    setIsGenerating(false);
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    showToast("Link copied to clipboard!");
  };

  const handleReset = () => {
    setCreatedTestId(null);
  };

  const fetchQuestionsForView = async (testId) => {
    setIsLoadingQuestions(true);
    try {
      const response = await getFlashTestQuestions(testId);
      const fetchedQuestions = Array.isArray(response?.data) ? response.data : (response?.data?.questions || []);
      const mappedQuestions = fetchedQuestions.map(q => {
        const resolvedId = q.id ?? q.questionId ?? q.question_id ?? q.qid ?? q.examQuestionId ?? null;
        return {
          ...q,
          id: resolvedId,
          question: q.question || "",
          code: q.code || "",
          options: q.options || { A: "", B: "", C: "", D: "" },
          correctOption: (q.correctOption || "A").toUpperCase(),
          answerText: q.answerText || ""
        };
      });
      setViewingQuestions(mappedQuestions);
    } catch (err) {
      console.error("Failed to load questions:", err);
      showToast("Failed to load questions from database.");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleViewTest = async (testId) => {
    setViewingTestId(testId);
    setHideActionsForTest(false);
    fetchQuestionsForView(testId);
    try {
      const response = await getFlashTestAllResults(testId);
      if (response && response.data) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setHideActionsForTest(true);
        } else if (typeof response.data === 'number' && response.data > 0) {
          setHideActionsForTest(true);
        } else if (response.data > 0) {
          setHideActionsForTest(true);
        }
      }
    } catch (err) {
      console.error("Failed to check if test has attempts:", err);
    }
  };

  const handleOpenAddModal = () => {
    setEditingQuestion({
      question: "",
      code: "",
      options: { A: "", B: "", C: "", D: "" },
      correctOption: "A",
      answerText: ""
    });
  };

  const handleOpenEditModal = (q) => {
    setEditingQuestion({
      ...q,
      options: {
        A: q.options?.A || q.options?.a || "",
        B: q.options?.B || q.options?.b || "",
        C: q.options?.C || q.options?.c || "",
        D: q.options?.D || q.options?.d || ""
      }
    });
  };

  const handleSaveQuestion = async (e) => {
    if (e) e.preventDefault();
    if (!editingQuestion.question.trim()) {
      showToast("Question text is required.");
      return;
    }
    if (!editingQuestion.options.A.trim() || !editingQuestion.options.B.trim() || !editingQuestion.options.C.trim() || !editingQuestion.options.D.trim()) {
      showToast("All four options (A, B, C, D) are required.");
      return;
    }
    if (!editingQuestion.answerText || !editingQuestion.answerText.trim()) {
      showToast("Explanation / Answer Text is required.");
      return;
    }

    setIsSubmittingQuestion(true);
    try {
      if (editingQuestion.id) {
        // Update question
        const payload = {
          id: editingQuestion.id,
          question: editingQuestion.question,
          code: editingQuestion.code,
          options: {
            A: editingQuestion.options.A,
            B: editingQuestion.options.B,
            C: editingQuestion.options.C,
            D: editingQuestion.options.D
          },
          correctOption: editingQuestion.correctOption,
          answerText: editingQuestion.answerText
        };
        await updateFlashTestQuestion(payload);
        showToast("Question updated successfully.");
      } else {
        // Add question
        const payload = {
          examId: viewingTestId,
          question: editingQuestion.question,
          code: editingQuestion.code,
          options: {
            A: editingQuestion.options.A,
            B: editingQuestion.options.B,
            C: editingQuestion.options.C,
            D: editingQuestion.options.D
          },
          correctOption: editingQuestion.correctOption,
          answerText: editingQuestion.answerText
        };
        await addFlashTestQuestion(payload);
        showToast("Question added successfully.");
      }
      setEditingQuestion(null);
      fetchQuestionsForView(viewingTestId);
    } catch (err) {
      console.error("Error saving question:", err);
      showToast("Error saving question: " + (err.response?.data || err.message));
    } finally {
      setIsSubmittingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) {
      return;
    }
    try {
      const payload = {
        questionId: questionId,
        examId: viewingTestId
      };
      await deleteFlashTestQuestion(payload);
      showToast("Question deleted successfully.");
      fetchQuestionsForView(viewingTestId);
    } catch (err) {
      console.error("Error deleting question:", err);
      showToast("Failed to delete question.");
    }
  };

  const handleDeleteTest = async (examId) => {
    if (!window.confirm("Are you sure you want to delete this exam?")) {
      return;
    }
    try {
      await deleteFlashTest(examId);
      showToast("Exam deleted successfully.");
      fetchRecruiterExams();
    } catch (err) {
      console.error("Error deleting exam:", err);
      showToast("Failed to delete exam.");
    }
  };

  const handleToggleStatus = async (testId, newStatus) => {
    try {
      await updateFlashTestStatus(testId, newStatus);
      showToast(`Test ${newStatus ? 'opened' : 'closed'} successfully.`);
      
      setAllTests(prevTests => {
        const updated = prevTests.map(t => t.id === testId ? { ...t, isActive: newStatus } : t);
        localStorage.setItem("allFlashTests", JSON.stringify(updated));
        return updated;
      });
    } catch (err) {
      console.error("Failed to update test status:", err);
      showToast("Failed to update test status.");
    }
  };

  const handleToggleShowAnswers = async (testId, newStatus) => {
    try {
      await updateFlashTestShowAnswers(testId, newStatus);
      setAllTests(prevTests => {
        const updated = prevTests.map(t => {
          if (t.id === testId) {
            const updatedTest = {
              ...t,
              settings: {
                ...(t.settings || {}),
                showAnswers: newStatus
              }
            };
            localStorage.setItem(`flashTest_${t.id}`, JSON.stringify(updatedTest));
            return updatedTest;
          }
          return t;
        });
        localStorage.setItem("allFlashTests", JSON.stringify(updated));
        return updated;
      });
      showToast(`Test review ${newStatus ? 'shown' : 'hidden'} successfully.`);
    } catch (err) {
      console.error("Failed to update test review status:", err);
      showToast("Failed to update test review status.");
    }
  };

  return (
    <div className="dashboard__content flash-test-container">

      {/* Page Header */}
      <h1 className="flash-page-title">Flash Test</h1>

      {/* Tabs Navigation - Rendered as divs to bypass global button CSS styles */}
      <div className="flash-tabs-nav">
        <div
          className={`flash-tab-btn ${activeTab === "questions" ? "active" : ""}`}
          onClick={() => setActiveTab("questions")}
          role="button"
          tabIndex={0}
        >
          Test Generator
        </div>
        <div
          className={`flash-tab-btn ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
          role="button"
          tabIndex={0}
        >
          Settings
        </div>
        <div
          className={`flash-tab-btn ${activeTab === "results" ? "active" : ""}`}
          onClick={() => setActiveTab("results")}
          role="button"
          tabIndex={0}
        >
          Results
        </div>
      </div>

      <div className="flash-test-card">
        {activeTab === "questions" && (
          viewingTestId ? (
            <div className="view-test-panel">
              {isLoadingQuestions ? (
                <div style={{ textAlign: "center", padding: "60px", color: "#94a3b8" }}>
                  <div style={{ fontSize: "36px", animation: "spin 2s linear infinite", display: "inline-block", marginBottom: "15px" }}>⚙️</div>
                  <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b", margin: "0 0 8px" }}>Loading test questions...</h3>
                  <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>Retrieving the questions from the database...</p>
                </div>
              ) : (
                <>
                  <div className="view-test-header">
                    <div className="view-test-title-section">
                      <h2>Test Questions Preview</h2>
                      <div className="view-test-meta-pills">
                        <span className="meta-pill difficulty">ID: {viewingTestId}</span>
                        <span className="meta-pill questions-count">{viewingQuestions.length} Questions</span>
                      </div>
                    </div>
                    <div className="view-test-actions">
                      <button
                        onClick={() => {
                          setViewingTestId(null);
                          setViewingQuestions([]);
                          setHideActionsForTest(false);
                          setSelectedTestIdForResults(null);
                          setActiveTab("results");
                        }}
                        className="btn-premium-back"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "8px 16px",
                          fontSize: "14px",
                          fontWeight: 600,
                          background: "rgb(241, 245, 249)",
                          border: "1px solid rgb(203, 213, 225)",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      >
                        ← Back
                      </button>
                      {!hideActionsForTest && (
                        <button
                          onClick={handleOpenAddModal}
                          className="btn-premium-add"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 16px",
                            fontSize: "14px",
                            fontWeight: 600,
                            background: "rgb(241, 245, 249)",
                            border: "1px solid rgb(203, 213, 225)",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                        >
                          Add Question
                        </button>
                      )}
                    </div>
                  </div>

                  {viewingQuestions.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                      No questions found in this test. Click "Add Question" to create one.
                    </div>
                  ) : (
                    <div className="view-questions-list">
                      {viewingQuestions.map((q, index) => (
                        <div key={q.id || index} className="view-question-card">
                          <div className="card-question-header">
                            <h3 className="card-question-text">
                              {index + 1}. <span dangerouslySetInnerHTML={{ __html: formatQuestionText(q.question) }} />
                            </h3>
                            {!hideActionsForTest && (
                              <div className="card-question-actions">
                                <button
                                  onClick={() => handleOpenEditModal(q)}
                                  className="btn-icon-edit"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "8px 16px",
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    background: "rgb(241, 245, 249)",
                                    border: "1px solid rgb(203, 213, 225)",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteQuestion(q.id)}
                                  className="btn-icon-delete"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "8px 16px",
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    background: "rgb(241, 245, 249)",
                                    border: "1px solid rgb(203, 213, 225)",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>

                          {q.code && (
                            <pre style={{
                              background: "#1e1e1e",
                              color: "#d4d4d4",
                              padding: "12px",
                              borderRadius: "6px",
                              fontFamily: "monospace",
                              overflowX: "auto",
                              marginTop: "10px",
                              marginBottom: "15px",
                              borderLeft: "4px solid #F97316",
                              textAlign: "left"
                            }}>
                              <code>{q.code}</code>
                            </pre>
                          )}

                          <div className="options-preview-grid">
                            {["A", "B", "C", "D"].map(optKey => {
                              const isCorrect = q.correctOption?.toUpperCase() === optKey;
                              const optionText = q.options?.[optKey] || q.options?.[optKey.toLowerCase()] || "";
                              return (
                                <div
                                  key={optKey}
                                  className={`option-preview-item ${isCorrect ? "correct" : ""}`}
                                >
                                  <span className="option-letter-badge">{optKey}</span>
                                  <span dangerouslySetInnerHTML={{ __html: String(optionText || "").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>").replace(/```/g, "") }} />
                                </div>
                              );
                            })}
                          </div>

                          {q.answerText && (
                            <div className="explanation-preview-box">
                              <strong>Explanation:</strong> {q.answerText}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : createdTestId ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: "64px", marginBottom: "20px" }}>🎉</div>
              <h2 className="flash-test-title" style={{ color: "#1aae55", marginBottom: "12px" }}>
                Flash Test Created
              </h2>
              <p className="flash-test-subtitle" style={{ maxWidth: "500px", margin: "0 auto 30px" }}>
                Your AI-powered assessment has been generated and is ready to share. Candidates can access the test using the dynamic link below.
              </p>

              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "12px 18px",
                maxWidth: "600px",
                margin: "0 auto 30px",
                gap: "10px"
              }}>
                <span style={{
                  color: "#475569",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flexGrow: 1,
                  textAlign: "left"
                }}>
                  {`${window.location.origin}/applicanthome/flashtest/${createdTestId}`}
                </span>
                <button
                  onClick={() => handleCopyLink(`${window.location.origin}/applicanthome/flashtest/${createdTestId}`)}
                  style={{
                    background: "#F97316",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    flexShrink: 0
                  }}
                >
                  Copy Link
                </button>
              </div>

              <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                <button
                  onClick={() => handleViewTest(createdTestId)}
                  className="btn-secondary"
                  style={{
                    background: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)",
                    color: "white",
                    border: "none",
                    padding: "10px 24px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 4px 10px rgba(249, 115, 22, 0.2)"
                  }}
                >
                  View Test
                </button>
                <button
                  onClick={handleReset}
                  className="btn-secondary"
                  style={{ padding: "10px 24px", borderRadius: "8px" }}
                >
                  Create Another Test
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="flash-test-title">Flash Test Generator</h2>
              <p className="flash-test-subtitle">
                Instantly create dynamic, AI-powered assessments.
                Fill in the details below and click generate.
              </p>

              {/* Loading overlay shown while generating */}
              {isGenerating && (
                <div style={{
                  background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
                  border: "2px solid #F97316",
                  borderRadius: "12px",
                  padding: "28px 24px",
                  marginBottom: "20px",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "36px", marginBottom: "10px", animation: "spin 2s linear infinite", display: "inline-block" }}>⚙️</div>
                  <p style={{ fontWeight: "700", color: "#c2410c", fontSize: "16px", margin: "0 0 6px" }}>
                    Generating your test...
                  </p>
                  <p style={{ color: "#9a3412", fontSize: "14px", margin: 0 }}>
                    {generateStatus || "Please wait, this can take up to 60 seconds."}
                  </p>
                  <div style={{ marginTop: "14px", display: "flex", justifyContent: "center", gap: "6px" }}>
                    {[0, 1, 2].map(i => (
                      <span key={i} style={{
                        width: "8px", height: "8px", borderRadius: "50%",
                        background: "#F97316",
                        display: "inline-block",
                        animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`
                      }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Inline error message from backend */}
              {generateError && !isGenerating && (
                <div style={{
                  background: "#fef2f2",
                  border: "1.5px solid #fca5a5",
                  borderRadius: "10px",
                  padding: "18px 20px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px"
                }}>
                  <span style={{ fontSize: "22px", flexShrink: 0 }}>⚠️</span>
                  <div>
                    <p style={{ fontWeight: "700", color: "#b91c1c", margin: "0 0 4px", fontSize: "15px" }}>Test generation failed</p>
                    <p style={{ color: "#991b1b", margin: 0, fontSize: "14px", lineHeight: "1.5" }}>{generateError}</p>
                  </div>
                  <button
                    onClick={() => setGenerateError("")}
                    style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontWeight: "700", fontSize: "18px", flexShrink: 0, lineHeight: 1 }}
                  >×</button>
                </div>
              )}

              <FlashTestForm onSubmit={handleGenerate} isGenerating={isGenerating} />
            </>
          )
        )}

        {activeTab === "settings" && (
          <div className="flash-settings-container">
            {/* Card 1: Test access */}
            <div className="settings-card">
              <h3 className="settings-card-title">Test access</h3>
              <div className="settings-row">
                <div className="settings-input-group">
                  <label>Passcode</label>
                  <input
                    type="text"
                    value={settings.passcode}
                    onChange={(e) => handleSettingChange("passcode", e.target.value)}
                    placeholder="Enter passcode..."
                  />
                </div>
              </div>
            </div>

            {/* Card 2: Scoring & results */}
            <div className="settings-card">
              <h3 className="settings-card-title">Scoring & results</h3>
              <div className="settings-row">
                <div className="settings-input-group">
                  <label>Passing score (%)</label>
                  <input
                    type="number"
                    value={settings.passingScore}
                    onChange={(e) => handleSettingChange("passingScore", parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="settings-input-group">
                  <label>Max attempts</label>
                  <input
                    type="number"
                    value={settings.maxAttempts}
                    onChange={(e) => handleSettingChange("maxAttempts", parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>

              <div className="settings-toggle-row">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.showScore}
                    onChange={(e) => handleSettingChange("showScore", e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">Show score after submission</span>
              </div>

              <div className="settings-toggle-row">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.showAnswers}
                    onChange={(e) => handleSettingChange("showAnswers", e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">Show correct answers after submission</span>
              </div>
            </div>

            {/* Card 3: Question display */}
            <div className="settings-card">
              <h3 className="settings-card-title">Question display</h3>

              <div className="settings-toggle-row">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.shuffleQuestions}
                    onChange={(e) => handleSettingChange("shuffleQuestions", e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">Shuffle question order</span>
              </div>

              <div className="settings-toggle-row">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.shuffleChoices}
                    onChange={(e) => handleSettingChange("shuffleChoices", e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">Shuffle answer choices</span>
              </div>
            </div>

            {/* Apply Button */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
              <button
                onClick={handleApplySettings}
                style={{
                  background: "linear-gradient(286deg, rgb(251, 187, 92) 0%, rgb(230, 106, 14) 100%)",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px 28px",
                  fontSize: "14.5px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.2), 0 2px 4px -1px rgba(79, 70, 229, 0.1)",
                  transition: "all 0.2s"
                }}
              >
                Apply Settings
              </button>
            </div>
          </div>
        )}

        {activeTab === "results" && !selectedTestIdForResults && (() => {
          const filteredTests = allTests
            .filter(test =>
              !testSearchQuery ||
              test.examName?.toLowerCase().includes(testSearchQuery.toLowerCase())
            )
            .sort((a, b) => {
              const toMs = (val) => {
                if (!val) return 0;
                if (Array.isArray(val) && val.length >= 3) {
                  const [y, mo, d, h = 0, min = 0, s = 0] = val;
                  return new Date(y, mo - 1, d, h, min, s).getTime();
                }
                const ms = new Date(val).getTime();
                return isNaN(ms) ? 0 : ms;
              };
              return toMs(b.createdAt) - toMs(a.createdAt);
            });

          return (
            <div className="flash-results-container">
              <div className="settings-card" style={{ padding: "20px", background: "white", borderRadius: "12px", boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)" }}>
                {/* Header & Search Bar Row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <h3 style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b", margin: 0 }}>All test details</h3>
                  <input
                    type="text"
                    placeholder="Search tests by name..."
                    value={testSearchQuery}
                    onChange={(e) => setTestSearchQuery(e.target.value)}
                    style={{
                      width: "250px",
                      padding: "8px 14px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                      color: "#1e293b",
                      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                    }}
                  />
                </div>

                {loadingTests && allTests.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                    <div style={{ fontSize: "28px", animation: "spin 2s linear infinite", display: "inline-block", marginBottom: "10px" }}>⚙️</div>
                    <p style={{ margin: 0, fontWeight: "600" }}>Syncing tests from database...</p>
                  </div>
                ) : (
                  <>
                    {fetchError && (
                      <div style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#b45309", padding: "10px 14px", borderRadius: "6px", marginBottom: "15px", fontSize: "13px", display: "flex", gap: "8px", alignItems: "center" }}>
                        <span>⚠️</span> <span>{fetchError}</span>
                      </div>
                    )}

                    {allTests.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                        No assessments have been generated yet. Go to the Questions tab to create one.
                      </div>
                    ) : (
                      <>
                        <div ref={allTestsTableContainerRef} className="hide-scrollbar" style={{ overflowX: "auto" }}>
                          <table className="results-table" style={{ width: "100%" }}>
                            <thead>
                              <tr style={{ backgroundColor: "#fbe5d6" }}>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>Details</th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>
                                  <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
                                    Test level
                                    <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "bold" }}></span>
                                  </div>
                                </th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>
                                  <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
                                    Question count
                                    <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "bold" }}></span>
                                  </div>
                                </th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>
                                  <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
                                    Time limit
                                    <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "bold" }}></span>
                                  </div>
                                </th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>Status</th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>Review</th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>Test link</th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>Edit</th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>View</th>
                                <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none", textAlign: "center", padding: "14px 16px", font: "normal normal 600 18px/ 27px Poppins" }}>Delete</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTests.length === 0 ? (
                                <tr>
                                  <td colSpan={9} style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                                    No tests match your search.
                                  </td>
                                </tr>
                              ) : (
                                filteredTests.map((test) => {
                                  const attemptsKey = `flashTestAttempts_${test.id}`;
                                  const attempts = JSON.parse(localStorage.getItem(attemptsKey) || "[]");

                                  // Fallback to legacy single attempt
                                  if (attempts.length === 0) {
                                    const attemptKey = `flashTestAttempt_${test.id}`;
                                    const attemptData = localStorage.getItem(attemptKey);
                                    if (attemptData) {
                                      attempts.push(JSON.parse(attemptData));
                                    }
                                  }

                                  return (
                                    <tr key={test.id}>
                                      <td style={{ padding: "14px 16px" }}>
                                        <div style={{ fontWeight: "700", color: "#1e293b" }}>{test.examName || test.subject}</div>
                                        <div style={{ fontSize: "12px", color: "#64748b" }}>{test.subject} ({test.category || "Technical"})</div>
                                        {test.createdAt && (
                                          <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                                            <span>📅</span> <span>{fmtDateTime(test.createdAt)}</span>
                                          </div>
                                        )}
                                      </td>
                                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                                        <span style={{
                                          fontWeight: "600",
                                          color: (test.difficulty?.toLowerCase() === "low" || test.difficulty?.toLowerCase() === "easy") ? "#22c55e" :
                                            (test.difficulty?.toLowerCase() === "high" || test.difficulty?.toLowerCase() === "hard") ? "#ef4444" : "#f97316"
                                        }}>
                                          {test.difficulty ? test.difficulty.charAt(0).toUpperCase() + test.difficulty.slice(1) : "Medium"}
                                        </span>
                                      </td>
                                      <td style={{ textAlign: "center", color: "#334155", padding: "14px 16px" }}>
                                        {test.questionCount ?? test.questions?.length ?? 10} Questions
                                      </td>
                                      <td style={{ textAlign: "center", color: "#334155", padding: "14px 16px" }}>
                                        {test.duration != null && test.duration !== 0 ? `${test.duration} mins` : (test.settings?.timeLimit ? `${test.settings.timeLimit} mins` : "Unlimited")}
                                      </td>
                                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                                        <label className="switch" style={{ transform: "scale(0.8)", margin: "0 auto", display: "block" }}>
                                          <input
                                            type="checkbox"
                                            checked={test.isActive !== false}
                                            onChange={() => handleToggleStatus(test.id, !(test.isActive !== false))}
                                          />
                                          <span className="slider round"></span>
                                        </label>
                                        <div style={{ fontSize: "10px", marginTop: "4px", color: test.isActive !== false ? "#10b981" : "#ef4444", fontWeight: "bold" }}>
                                          {test.isActive !== false ? "OPEN" : "CLOSED"}
                                        </div>
                                      </td>
                                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                                        <label className="switch" style={{ transform: "scale(0.8)", margin: "0 auto", display: "block" }}>
                                          <input
                                            type="checkbox"
                                            checked={test.settings?.showAnswers !== false}
                                            onChange={() => handleToggleShowAnswers(test.id, !(test.settings?.showAnswers !== false))}
                                          />
                                          <span className="slider round"></span>
                                        </label>
                                        <div style={{ fontSize: "10px", marginTop: "4px", color: test.settings?.showAnswers !== false ? "#10b981" : "#ef4444", fontWeight: "bold" }}>
                                          {test.settings?.showAnswers !== false ? "SHOW" : "HIDE"}
                                        </div>
                                      </td>
                                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                                        <button
                                          onClick={() => {
                                            const link = `${window.location.origin}/applicanthome/flashtest/${test.id}`;
                                            navigator.clipboard.writeText(link);
                                            showToast("Link copied to clipboard!");
                                          }}
                                          style={{
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            width: "32px",
                                            height: "32px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            transition: "opacity 0.2s"
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                                          title="Copy Test Link"
                                        >
                                          <img src={linkRecruiterIcon} alt="Copy Link" style={{ width: "32px", height: "32px", display: "block" }} />
                                        </button>
                                      </td>
                                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                                        <button
                                          onClick={() => {
                                            setActiveTab("questions");
                                            setTimeout(() => {
                                              handleViewTest(test.id);
                                            }, 50);
                                          }}
                                          style={{
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            width: "32px",
                                            height: "32px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            transition: "opacity 0.2s"
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                                          title="Edit Test"
                                        >
                                          <img src={editRecruiterIcon} alt="Edit" style={{ width: "32px", height: "32px", display: "block" }} />
                                        </button>
                                      </td>
                                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                                        <button
                                          onClick={() => {
                                            handleViewResults(test.id);
                                          }}
                                          style={{
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            width: "32px",
                                            height: "32px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            transition: "opacity 0.2s"
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                                          title="View Details"
                                        >
                                          <img src={viewRecruiterIcon} alt="View" style={{ width: "32px", height: "32px", display: "block" }} />
                                        </button>
                                      </td>
                                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                                        <button
                                          onClick={() => {
                                            handleDeleteTest(test.id);
                                          }}
                                          style={{
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            width: "32px",
                                            height: "32px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            transition: "opacity 0.2s"
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                                          title="Delete Test"
                                        >
                                          <img src={deleteRecruiterIcon} alt="Delete" style={{ width: "32px", height: "32px", display: "block" }} />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                }))}
                            </tbody>
                          </table>
                        </div>
                        <StickyHorizontalScrollbar targetRef={allTestsTableContainerRef} />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {activeTab === "results" && selectedTestIdForResults && (() => {
          if (loadingAttempts) {
            return (
              <div className="flash-results-container" style={{ textAlign: "center", padding: "60px", color: "#94a3b8" }}>
                <div style={{ fontSize: "36px", animation: "spin 2s linear infinite", display: "inline-block", marginBottom: "15px" }}>⚙️</div>
                <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b", margin: "0 0 8px" }}>Loading Candidate Attempts</h3>
                <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>Fetching live submission details from database...</p>
              </div>
            );
          }

          const test = allTests.find(t => t.id === selectedTestIdForResults);
          if (!test) return null;

          const attempts = fetchedAttempts || [];
          const displayQuestions = getDisplayQuestions(resultsQuestions, attempts, test);

          const totalQs = (selectedAttemptForReview && reviewQuestions.length > 0) ? reviewQuestions.length : (test.questions?.length || test.questionCount || 10);
          const passingScore = test.settings?.passingScore ?? 70;

          if (selectedAttemptForReview) {
            const isPassed = selectedAttemptForReview.score >= passingScore;

            return (
              <div className="flash-results-container">
                {/* Header with Back Button */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "center" }}>
                    <button
                      onClick={() => { setSelectedAttemptForReview(null); setReviewQuestions([]); }}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        padding: "8px 16px", fontSize: "14px", fontWeight: "600",
                        background: "#f1f5f9", border: "1px solid #cbd5e1",
                        borderRadius: "8px", cursor: "pointer", marginBottom: "16px"
                      }}
                    >
                      ← Back
                    </button>
                  </div>
                  <div>
                    <h2 style={{ fontSize: "22px", fontWeight: "800", color: "#1e293b", marginBottom: "4px" }}>
                      Candidate Test Review — {selectedAttemptForReview.name || "Anonymous"}
                    </h2>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "13.5px", color: "#475569", marginTop: "8px" }}>
                      <div><strong>Register Number:</strong> {selectedAttemptForReview.regno || "N/A"}</div>
                      <div><strong>Email Address:</strong> {selectedAttemptForReview.email || "N/A"}</div>
                      <div><strong>Phone Number:</strong> {selectedAttemptForReview.phoneno || "N/A"}</div>
                      <div><strong>College / Institution:</strong> {selectedAttemptForReview.college || "N/A"}</div>
                      <div><strong>Branch:</strong> {selectedAttemptForReview.branch || "N/A"}</div>
                    </div>
                  </div>
                </div>

                {/* Individual Stats Row */}
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "16px", marginBottom: "26px"
                }}>
                  {/* Score Card */}
                  <div className="settings-card" style={{ padding: "18px" }}>
                    <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "700", textTransform: "uppercase", marginBottom: "8px" }}>Score</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                      <span style={{ fontSize: "28px", fontWeight: "800", color: isPassed ? "#10b981" : "#ef4444" }}>
                        {selectedAttemptForReview.score}%
                      </span>
                      <span style={{
                        fontSize: "11px", padding: "2px 6px", borderRadius: "10px",
                        fontWeight: "600",
                        background: isPassed ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                        color: isPassed ? "#10b981" : "#ef4444"
                      }}>
                        {isPassed ? "Passed" : "Failed"}
                      </span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>Passing Threshold: {passingScore}%</div>
                  </div>

                  {/* Time Card */}
                  <div className="settings-card" style={{ padding: "18px" }}>
                    <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "700", textTransform: "uppercase", marginBottom: "8px" }}>Duration & Time</div>
                    <div style={{ fontSize: "24px", fontWeight: "800", color: "#1e293b" }}>
                      {fmtDuration(selectedAttemptForReview.durationMs)}
                    </div>
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>
                      Date: {fmtDateTime(selectedAttemptForReview.endTime || selectedAttemptForReview.startTime || selectedAttemptForReview.timestamp || selectedAttemptForReview.finishedOn)}
                    </div>
                  </div>

                  {/* Question Stats Card */}
                  <div className="settings-card" style={{ padding: "18px" }}>
                    <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "700", textTransform: "uppercase", marginBottom: "8px" }}>Question Stats</div>
                    <div style={{ display: "flex", gap: "16px", marginTop: "4px" }}>
                      <div>
                        <div style={{ fontSize: "18px", fontWeight: "800", color: "#10b981" }}>
                          {selectedAttemptForReview.correctCount !== undefined ? selectedAttemptForReview.correctCount : Math.round((selectedAttemptForReview.score / 100) * totalQs)}
                        </div>
                        <div style={{ fontSize: "10px", color: "#94a3b8" }}>Correct</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "18px", fontWeight: "800", color: "#ef4444" }}>
                          {selectedAttemptForReview.wrongCount !== undefined ? selectedAttemptForReview.wrongCount : (totalQs - (selectedAttemptForReview.correctCount !== undefined ? selectedAttemptForReview.correctCount : Math.round((selectedAttemptForReview.score / 100) * totalQs)))}
                        </div>
                        <div style={{ fontSize: "10px", color: "#94a3b8" }}>Wrong</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "18px", fontWeight: "800", color: "#64748b" }}>
                          {selectedAttemptForReview.notAnsweredCount !== undefined ? selectedAttemptForReview.notAnsweredCount : 0}
                        </div>
                        <div style={{ fontSize: "10px", color: "#94a3b8" }}>Not Answered</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "18px", fontWeight: "800", color: "#64748b" }}>
                          {totalQs}
                        </div>
                        <div style={{ fontSize: "10px", color: "#94a3b8" }}>Total Qs</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Question List */}
                <h3 style={{ fontSize: "16px", fontWeight: "700", marginBottom: "16px", color: "#1e293b", textAlign: "left" }}>Question Breakdown</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  {(reviewQuestions.length > 0 ? reviewQuestions : (test.questions || [])).map((q, index) => {
                    const hasResult = Array.isArray(selectedAttemptForReview.questionResults) && selectedAttemptForReview.questionResults.length > index;
                    const isCorrect = hasResult ? selectedAttemptForReview.questionResults[index] : null;
                    const isNotAttempted = selectedAttemptForReview.selectedOptions &&
                      (selectedAttemptForReview.selectedOptions[index] === null ||
                        selectedAttemptForReview.selectedOptions[index] === undefined ||
                        selectedAttemptForReview.selectedOptions[index] === "");

                    return (
                      <div key={index} style={{
                        padding: "20px",
                        borderRadius: "10px",
                        border: isNotAttempted ? "1px solid #cbd5e1" : "1px solid #e2e8f0",
                        background: isNotAttempted ? "#f1f5f9" : "#ffffff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                      }}>
                        {/* Question Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                          <span style={{ fontSize: "13px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>
                            Question {index + 1}
                          </span>
                          {(() => {
                            const isNotAttempted = selectedAttemptForReview.selectedOptions &&
                              (selectedAttemptForReview.selectedOptions[index] === null ||
                                selectedAttemptForReview.selectedOptions[index] === undefined ||
                                selectedAttemptForReview.selectedOptions[index] === "");

                            if (isNotAttempted) {
                              return (
                                <span style={{
                                  fontSize: "11px", padding: "2px 8px", borderRadius: "10px",
                                  fontWeight: "700",
                                  background: "rgba(100,116,139,0.1)",
                                  color: "#64748b"
                                }}>
                                  ✗ Not Answered
                                </span>
                              );
                            }

                            if (isCorrect !== null) {
                              return (
                                <span style={{
                                  fontSize: "11px", padding: "2px 8px", borderRadius: "10px",
                                  fontWeight: "700",
                                  background: isCorrect ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                                  color: isCorrect ? "#10b981" : "#ef4444"
                                }}>
                                  {isCorrect ? "✓ Correct" : "✗ Incorrect"}
                                </span>
                              );
                            }

                            return null;
                          })()}
                        </div>

                        {/* Question Text */}
                        <h4 style={{ fontSize: "15px", fontWeight: "600", marginBottom: "16px", color: "#1e293b", textAlign: "left", lineHeight: "1.6" }}>
                          <span dangerouslySetInnerHTML={{ __html: formatQuestionText(q.question) }} />
                        </h4>
                        {q.code && (
                          <pre className="code-snippet-box" style={{ background: "#1e1e1e", color: "#d4d4d4", padding: "12px", borderRadius: "6px", fontFamily: "monospace", textAlign: "left", overflowX: "auto", margin: "15px 0", borderLeft: "4px solid #F68318;" }}>
                            <code>{q.code}</code>
                          </pre>
                        )}

                        {/* Options */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {(() => {
                            let optionsEntries = [];
                            if (q.options && typeof q.options === "object" && !Array.isArray(q.options)) {
                              optionsEntries = Object.entries(q.options).map(([key, val]) => [key.toUpperCase(), val]);
                            } else if (Array.isArray(q.options)) {
                              const keys = ["A", "B", "C", "D"];
                              optionsEntries = q.options.map((val, idx) => [keys[idx] || "", val]);
                            }

                            return optionsEntries.map(([optionKey, optionText]) => {
                              const isSelected = selectedAttemptForReview.selectedOptions ? (selectedAttemptForReview.selectedOptions[index] === optionKey) : false;
                              const isCorrectOption = optionKey === (q.correctOption || "A");

                              let optionStyle = {
                                padding: "12px 16px",
                                borderRadius: "8px",
                                border: "1px solid #cbd5e1",
                                background: "#ffffff",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                fontSize: "14px",
                                textAlign: "left",
                                lineHeight: "1.5"
                              };

                              let icon = null;
                              let statusText = null;

                              if (selectedAttemptForReview.selectedOptions) {
                                if (isSelected && isCorrectOption) {
                                  optionStyle.border = "2px solid #10b981";
                                  optionStyle.background = "#ecfdf5";
                                  optionStyle.color = "#065f46";
                                  optionStyle.fontWeight = "600";
                                  icon = <span style={{ color: "#10b981", fontWeight: "bold" }}>✓</span>;
                                  statusText = <span style={{ marginLeft: "auto", color: "#10b981", fontSize: "11px", fontWeight: "700", flexShrink: 0 }}>Candidate's Selection & Correct Answer</span>;
                                } else if (isSelected && !isCorrectOption) {
                                  optionStyle.border = "2px solid #ef4444";
                                  optionStyle.background = "#fef2f2";
                                  optionStyle.color = "#991b1b";
                                  optionStyle.fontWeight = "600";
                                  icon = <span style={{ color: "#ef4444", fontWeight: "bold" }}>✗</span>;
                                  statusText = <span style={{ marginLeft: "auto", color: "#ef4444", fontSize: "11px", fontWeight: "700", flexShrink: 0 }}>Candidate's Selection</span>;
                                } else if (!isSelected && isCorrectOption) {
                                  optionStyle.border = "2px solid #10b981";
                                  optionStyle.background = "#ecfdf5";
                                  optionStyle.color = "#065f46";
                                  optionStyle.fontWeight = "600";
                                  icon = <span style={{ color: "#10b981", fontWeight: "bold" }}>✓</span>;
                                  statusText = <span style={{ marginLeft: "auto", color: "#10b981", fontSize: "11px", fontWeight: "700", flexShrink: 0 }}>Correct Answer</span>;
                                }
                              } else {
                                // Legacy attempt fallback
                                if (isCorrectOption) {
                                  optionStyle.border = "2px solid #10b981";
                                  optionStyle.background = "#ecfdf5";
                                  optionStyle.color = "#065f46";
                                  optionStyle.fontWeight = "600";
                                  icon = <span style={{ color: "#10b981", fontWeight: "bold" }}>✓</span>;
                                  statusText = <span style={{ marginLeft: "auto", color: "#10b981", fontSize: "11px", fontWeight: "700", flexShrink: 0 }}>Correct Answer</span>;
                                }
                              }

                              return (
                                <div key={optionKey} style={optionStyle}>
                                  {icon}
                                  <span><strong>{optionKey}.</strong> </span>
                                  <span dangerouslySetInnerHTML={{ __html: String(optionText || "").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>").replace(/```/g, "") }} />
                                  {statusText}
                                </div>
                              );
                            });
                          })()}
                        </div>

                        {/* Legacy Notice if selectedOptions is missing */}
                        {!selectedAttemptForReview.selectedOptions && (
                          <div style={{
                            marginTop: "12px",
                            padding: "10px 14px",
                            background: "#f8fafc",
                            border: "1px dashed #cbd5e1",
                            borderRadius: "6px",
                            fontSize: "12px",
                            color: "#64748b",
                            textAlign: "left"
                          }}>
                            ℹ️ <em>Selected option details are not available for this older attempt. Based on the correctness grid, the candidate answered this question <strong>{isCorrect ? "Correctly" : "Incorrectly"}</strong>.</em>
                          </div>
                        )}

                        {/* Explanation block */}
                        {q.explanation && q.explanation.trim() !== "" && (
                          <div style={{
                            marginTop: "16px",
                            background: "#eff6ff",
                            borderLeft: "4px solid #3b82f6",
                            borderRadius: "0 6px 6px 0",
                            padding: "12px 16px",
                            fontSize: "13.5px",
                            color: "#1e3a8a",
                            textAlign: "left"
                          }}>
                            <strong>Explanation:</strong> <span dangerouslySetInnerHTML={{ __html: formatQuestionText(q.explanation) }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          const {
            completedAttempts,
            avgScore,
            minScore,
            maxScore,
            avgDurationMs,
            minDurationMs,
            maxDurationMs,
            buckets,
            maxBucket,
            qCorrectPct
          } = summarizeExamResults(attempts, totalQs);

          const uniqueDates = Array.from(new Set(
            attempts.map(a => {
              const formatted = formatDateTimeImage(a.startTime || a.start_time || a.startedOn || a.startedAt);
              return formatted !== "—" ? formatted.split(',')[0] : null;
            }).filter(Boolean)
          )).sort((a, b) => new Date(b) - new Date(a));

          const filteredAttempts = attempts.filter(a => {
            let matchesSearch = !searchFilter ||
              a.name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
              a.college?.toLowerCase().includes(searchFilter.toLowerCase());

            let matchesDate = true;
            if (dateFilter) {
              const formatted = formatDateTimeImage(a.startTime || a.start_time || a.startedOn || a.startedAt);
              const datePart = formatted !== "—" ? formatted.split(',')[0] : null;
              matchesDate = datePart === dateFilter;
            }
            return matchesSearch && matchesDate;
          });

          const sortedAttempts = [...filteredAttempts].sort((a, b) => {
            const scoreA = typeof a.score === "number" ? a.score : -1;
            const scoreB = typeof b.score === "number" ? b.score : -1;
            return scoreB - scoreA;
          });

          const bucketLabels = ["0–20%", "21–40%", "41–59%", "60–100%"];
          const bucketColors = ["#ef4444", "#f97316", "#eab308", "#22c55e"];
          const exportCSV = () => {
            const headers = [
              'ID',
              'BRANCH',
              'COLLEGE',
              'EMAIL',
              'NAME',
              'SCORE',
              'START TIME',
              'END TIME',
              'DURATION',
              'TAB SWITCH',
              'QUESTIONS'
            ];

            const escapeCSVField = (field) => {
              if (typeof field !== 'string') {
                return ' ';
              }
              if (field.trim() === '') {
                return ' ';
              }
              if (field.includes(',') || field.includes('"') || field.includes('\n')) {
                return `"${field.replace(/"/g, '""')}"`;
              }
              return field;
            };

            const rows = sortedAttempts.map((att, idx) => {
              const rowData = [];
              // ID
              rowData.push(escapeCSVField(String(att.id || idx + 1)));
              // BRANCH
              rowData.push(escapeCSVField(att.branch || 'N/A'));
              // COLLEGE
              rowData.push(escapeCSVField(att.college || 'N/A'));
              // EMAIL
              rowData.push(escapeCSVField(att.email || 'N/A'));
              // NAME
              rowData.push(escapeCSVField(att.name || 'Anonymous'));
              // SCORE
              rowData.push(escapeCSVField(att.score !== null && att.score !== undefined ? `${att.score}%` : 'NULL'));

              // START TIME
              rowData.push(escapeCSVField(fmtDateTime(att.startTime || att.start_time || att.startedOn || att.startedAt)));
              // END TIME
              rowData.push(escapeCSVField(fmtDateTime(att.endTime || att.end_time || att.finishedOn || att.finishedAt || att.timestamp)));

              // DURATION
              let durationMs = att.durationMs;
              if (!durationMs && att.startTime && att.endTime) {
                let startD, endD;
                if (Array.isArray(att.startTime) && att.startTime.length >= 3) {
                  const [y, m, d, h = 0, min = 0, s = 0] = att.startTime;
                  startD = new Date(y, m - 1, d, h, min, s);
                } else {
                  startD = new Date(att.startTime);
                }
                if (Array.isArray(att.endTime) && att.endTime.length >= 3) {
                  const [y, m, d, h = 0, min = 0, s = 0] = att.endTime;
                  endD = new Date(y, m - 1, d, h, min, s);
                } else {
                  endD = new Date(att.endTime);
                }
                if (!isNaN(startD.getTime()) && !isNaN(endD.getTime())) {
                  durationMs = endD.getTime() - startD.getTime();
                }
              }
              let durationText = "—";
              if (durationMs) {
                const totalSec = Math.floor(durationMs / 1000);
                const h = Math.floor(totalSec / 3600);
                const m = Math.floor((totalSec % 3600) / 60);
                const s = totalSec % 60;
                const hStr = h > 0 ? `${h}h ` : "";
                const mStr = m > 0 || h > 0 ? `${m}m ` : "";
                const sStr = `${s}s`;
                durationText = `${hStr}${mStr}${sStr}`;
              }
              rowData.push(escapeCSVField(durationText));

              // TAB SWITCH
              rowData.push(escapeCSVField(String(att.tabswitch ?? 0)));

              // QUESTIONS
              const questionStatuses = [];
              if (Array.isArray(att.questionDetails) && att.questionDetails.length > 0) {
                att.questionDetails.forEach((detail, detailIdx) => {
                  const userOpt = detail.userOption ? detail.userOption.toUpperCase() : null;
                  const isNotAnswered = !userOpt ||
                    userOpt === "" ||
                    userOpt === "NULL" ||
                    userOpt === "NA" ||
                    userOpt === "N/A" ||
                    userOpt === "—" ||
                    userOpt === "-" ||
                    userOpt === "—";
                  const correctOpt = (detail.correctOption || "").toUpperCase();
                  const isCorrect = userOpt && correctOpt && userOpt === correctOpt;

                  let status = "Not Answered";
                  if (!isNotAnswered) {
                    status = isCorrect ? "Correct" : "Incorrect";
                  }
                  questionStatuses.push(`Q${detailIdx + 1}: ${status}`);
                });
              } else if (Array.isArray(att.questionResults) && att.questionResults.length > 0) {
                att.questionResults.forEach((isCorrect, qIndex) => {
                  const userOpt = att.selectedOptions?.[qIndex] || null;
                  const isNotAnswered = !userOpt ||
                    userOpt === "" ||
                    userOpt === "NULL" ||
                    userOpt === "NA" ||
                    userOpt === "N/A" ||
                    userOpt === "—" ||
                    userOpt === "-" ||
                    userOpt === "—";

                  let status = "Not Answered";
                  if (!isNotAnswered) {
                    status = isCorrect ? "Correct" : "Incorrect";
                  }
                  questionStatuses.push(`Q${qIndex + 1}: ${status}`);
                });
              } else {
                for (let i = 0; i < totalQs; i++) {
                  questionStatuses.push(`Q${i + 1}: N/A`);
                }
              }
              rowData.push(escapeCSVField(questionStatuses.join('; ')));

              return rowData;
            });

            const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${test.examName || 'test'}_results.csv`;
            link.click();
          };

          const totalCount = completedAttempts.length;
          const detailBuckets = [0, 0, 0, 0];
          completedAttempts.forEach(a => {
            const score = a.score || 0;
            let b = 0;
            if (score <= 20) b = 0;
            else if (score <= 40) b = 1;
            else if (score < 60) b = 2;
            else b = 3;
            detailBuckets[b]++;
          });
          const maxBucketVal = Math.max(...detailBuckets, 1);

          let avgMinsText = "0.0 MINS";
          if (completedAttempts.length === 0) {
            avgMinsText = "0 MINS";
          } else if (avgDurationMs) {
            avgMinsText = `${(avgDurationMs / 60000).toFixed(1)} MINS`;
          } else if (test.duration) {
            avgMinsText = `${parseFloat(test.duration).toFixed(1)} MINS`;
          }

          return (
            <div className="flash-results-container">
              {/* ── Header ── */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button
                    onClick={() => { setSelectedTestIdForResults(null); setSearchFilter(""); setDateFilter(""); }}
                    style={{
                      background: "#f97316",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      width: "36px",
                      height: "36px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "20px",
                      fontWeight: "bold"
                    }}
                    title="Back to Tests"
                  >
                    ‹
                  </button>
                  <h2 style={{ fontSize: "22px", fontWeight: "800", color: "#1e293b", margin: 0 }}>
                    {test.examName || `${test.subject} ${test.category} Test`}
                  </h2>
                </div>
                <button
                  className="export-buttonn"
                  onClick={exportCSV}
                  style={{
                    background: "#f97316",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                  }}
                >
                  Export
                </button>
              </div>

              {/* ── Metrics Row ── */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 1.2fr 1.2fr 2.4fr",
                gap: "20px",
                marginBottom: "24px"
              }}>
                {/* Average Score */}
                <div className="settings-card" style={{ padding: "18px", display: "flex", flexDirection: "column" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "800", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.05em" }}>
                    AVERAGE SCORE
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "500", marginBottom: "16px" }}>
                    {minScore}% to {maxScore}%
                  </div>
                  <div style={{ fontSize: "36px", fontWeight: "800", color: "#f97316", lineHeight: "1.1", marginTop: "auto" }}>
                    {avgScore}%
                  </div>
                </div>

                {/* Average Time */}
                <div className="settings-card" style={{ padding: "18px", display: "flex", flexDirection: "column" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "800", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.05em" }}>
                    AVERAGE TIME
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "500", marginBottom: "16px" }}>
                    Average duration of attempts
                  </div>
                  <div style={{ fontSize: "36px", fontWeight: "800", color: "#f97316", lineHeight: "1.1", marginTop: "auto" }}>
                    {avgMinsText}
                  </div>
                </div>

                {/* Responses */}
                <div className="settings-card" style={{ padding: "18px", display: "flex", flexDirection: "column" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "800", textTransform: "uppercase", marginBottom: "2px", letterSpacing: "0.05em" }}>
                    RESPONSES
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "500", marginBottom: "16px" }}>
                    Passing threshold: {passingScore}%
                  </div>
                  <div style={{ fontSize: "36px", fontWeight: "800", color: "#f97316", lineHeight: "1.1", marginTop: "auto" }}>
                    {completedAttempts.length}
                  </div>
                </div>

                {/* Score Histogram */}
                <div className="settings-card" style={{ padding: "18px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "800", textTransform: "uppercase", marginBottom: "12px", letterSpacing: "0.05em" }}>
                    SCORE HISTOGRAM
                  </div>
                  {totalCount === 0 ? (
                    <div style={{ width: "100%", background: "#f1f5f9", borderRadius: "6px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "11px", fontWeight: "600", marginBottom: "12px" }}>
                      No responses yet
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "6px", width: "100%", height: "24px", marginBottom: "12px" }}>
                      {detailBuckets.map((count, bi) => {
                        const pct = totalCount > 0 ? (count / totalCount) * 100 : 0;
                        if (pct === 0) return null;
                        const displayCount = count < 10 ? `0${count}` : String(count);
                        return (
                          <div
                            key={bi}
                            className="histogram-segment-container"
                            style={{
                              width: `${pct}%`,
                              background: bucketColors[bi],
                              borderRadius: "4px",
                              height: "100%"
                            }}
                          >
                            <div className="histogram-tooltip">{displayCount}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                    {bucketLabels.map((label, bi) => (
                      <div key={bi} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", color: "#64748b" }}>
                        <span style={{ display: "inline-block", width: "10px", height: "10px", background: bucketColors[bi], borderRadius: "2px" }} />
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Search Bar & Filter ── */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder="Filter by name or college..."
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                    style={{
                      width: "360px", padding: "10px 14px",
                      border: "1px solid #cbd5e1", borderRadius: "8px",
                      fontSize: "14px", outline: "none", color: "#1e293b"
                    }}
                  />
                  
                  {uniqueDates.length > 0 && (
                    <select
                      value={dateFilter}
                      onChange={e => setDateFilter(e.target.value)}
                      style={{
                        padding: "10px 14px",
                        border: "1px solid #cbd5e1", borderRadius: "8px",
                        fontSize: "14px", outline: "none", color: "#1e293b",
                        backgroundColor: "#fff", cursor: "pointer", minWidth: "180px"
                      }}
                    >
                      <option value="">All Dates</option>
                      {uniqueDates.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* ── Per-student Grid ── */}
              <div className="settings-card" style={{ padding: "0", overflow: "hidden", maxWidth: "100%" }}>
                <div ref={attemptsTableContainerRef} className="hide-scrollbar" style={{ overflowX: "auto", width: "100%" }}>
                  <table className="results-table" style={{ width: "100%" }}>
                    <thead style={{ backgroundColor: "#fbe5d6" }}>
                      <tr style={{ backgroundColor: "#fbe5d6" }}>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textAlign: "center", textTransform: "none" }}>ID</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none" }}>College</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none" }}>Branch</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none" }}>Email</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textTransform: "none" }}>Name</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textAlign: "center", textTransform: "none" }}>Score</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textAlign: "center", textTransform: "none" }}>Start time</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textAlign: "center", textTransform: "none" }}>End time</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textAlign: "center", textTransform: "none" }}>Duration</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textAlign: "center", textTransform: "none" }}>Tab switch</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textAlign: "center", textTransform: "none" }}>Questions</th>
                        <th style={{ backgroundColor: "#fbe5d6", color: "#1e293b", fontWeight: "700", textAlign: "center", textTransform: "none" }}>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAttempts.length === 0 ? (
                        <tr>
                          <td colSpan={12} style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                            {attempts.length === 0 ? "No candidates have registered or completed this test yet." : "No results match your search."}
                          </td>
                        </tr>
                      ) : (
                        sortedAttempts.map((att, idx) => {
                          const isPassed = att.score !== null && att.score !== undefined && att.score >= passingScore;
                          const hasAttempt = att.score !== null && att.score !== undefined;

                          // Calculate duration in ms if not present but we have start and end times
                          let durationMs = att.durationMs;
                          if (!durationMs && att.startTime && att.endTime) {
                            let startD, endD;
                            if (Array.isArray(att.startTime) && att.startTime.length >= 3) {
                              const [y, m, d, h = 0, min = 0, s = 0] = att.startTime;
                              startD = new Date(y, m - 1, d, h, min, s);
                            } else {
                              startD = new Date(att.startTime);
                            }
                            if (Array.isArray(att.endTime) && att.endTime.length >= 3) {
                              const [y, m, d, h = 0, min = 0, s = 0] = att.endTime;
                              endD = new Date(y, m - 1, d, h, min, s);
                            } else {
                              endD = new Date(att.endTime);
                            }
                            if (!isNaN(startD.getTime()) && !isNaN(endD.getTime())) {
                              durationMs = endD.getTime() - startD.getTime();
                            }
                          }
                          return (
                            <tr key={idx}>
                              <td style={{ textAlign: "center", color: "#64748b", fontSize: "13px" }}>{att.id || idx + 1}</td>
                              <td style={{ fontSize: "13px", color: "#334155" }}>{att.college || "N/A"}</td>
                              <td style={{ fontSize: "13px", color: "#334155" }}>{att.branch || "N/A"}</td>
                              <td style={{ fontSize: "13px", color: "#334155" }}>{att.email || "N/A"}</td>
                              <td
                                onClick={() => handleLoadCandidateReview(att)}
                                style={{ cursor: hasAttempt ? "pointer" : "default", fontSize: "13px" }}
                                title={hasAttempt ? "Click to view candidate's test review" : "No attempt details available"}
                              >
                                <div style={{
                                  fontWeight: "600",
                                  color: hasAttempt ? "#5850ec" : "#64748b",
                                  textDecoration: hasAttempt ? "underline" : "none"
                                }}>
                                  {att.name || "Anonymous"}
                                </div>
                              </td>
                              <td style={{ textAlign: "center", fontSize: "13px", fontWeight: "700", color: hasAttempt ? getScoreColor(att.score, passingScore) : "#64748b" }}>
                                {hasAttempt ? `${att.score}%` : "NULL"}
                              </td>
                              <td style={{ textAlign: "center", fontSize: "13px", color: "#334155" }}>
                                {formatDateTimeImage(att.startTime || att.start_time || att.startedOn || att.startedAt)}
                              </td>
                              <td style={{ textAlign: "center", fontSize: "13px", color: "#334155" }}>
                                {formatDateTimeImage(att.endTime || att.end_time || att.finishedOn || att.finishedAt || att.timestamp)}
                              </td>
                              <td style={{ textAlign: "center", fontSize: "13px", color: "#334155" }}>
                                {formatDurationImage(durationMs)}
                              </td>
                              <td style={{ textAlign: "center", fontSize: "13px", color: "#f97316", fontWeight: "700" }}>
                                {att.tabswitch ?? 0}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center", flexWrap: "nowrap" }}>
                                  {(() => {
                                    const badges = [];

                                    if (Array.isArray(att.questionDetails) && att.questionDetails.length > 0) {
                                      att.questionDetails.forEach((detail, detailIdx) => {
                                        const userOpt = detail.userOption ? detail.userOption.toUpperCase() : null;
                                        const isNotAnswered = !userOpt ||
                                          userOpt === "" ||
                                          userOpt === "NULL" ||
                                          userOpt === "NA" ||
                                          userOpt === "N/A" ||
                                          userOpt === "—" ||
                                          userOpt === "-" ||
                                          userOpt === "—";
                                        const correctOpt = (detail.correctOption || "").toUpperCase();
                                        const isCorrect = userOpt && correctOpt && userOpt === correctOpt;

                                        const qTextClean = cleanText(detail.questionText);
                                        const qFromFetched = resultsQuestions?.find(tq => cleanText(tq.question) === qTextClean) ||
                                          test.questions?.find(tq => cleanText(tq.question) === qTextClean);

                                        let options = {};
                                        if (qFromFetched && qFromFetched.options) {
                                          if (typeof qFromFetched.options === "object" && !Array.isArray(qFromFetched.options)) {
                                            Object.entries(qFromFetched.options).forEach(([k, v]) => {
                                              options[k.toUpperCase()] = v;
                                            });
                                          } else if (Array.isArray(qFromFetched.options)) {
                                            const keys = ["A", "B", "C", "D"];
                                            qFromFetched.options.forEach((val, idx) => {
                                              options[keys[idx]] = val;
                                            });
                                          }
                                        } else {
                                          if (detail.userOption) {
                                            options[detail.userOption.toUpperCase()] = detail.userAnswerText || "";
                                          }
                                          if (detail.correctOption) {
                                            options[detail.correctOption.toUpperCase()] = detail.correctAnswerText || "";
                                          }
                                        }

                                        const matchingQ = {
                                          question: qFromFetched?.question || detail.questionText || "",
                                          code: qFromFetched?.code || "",
                                          options: options,
                                          correctOption: (qFromFetched?.correctOption || detail.correctOption || "A").toUpperCase(),
                                          answerText: qFromFetched?.answerText || detail.correctAnswerText || ""
                                        };

                                        let label = "Not Answered";
                                        let boxBg = "#ffffff";
                                        let boxBorder = "#cbd5e1";
                                        let boxColor = "#94a3b8";

                                        if (!isNotAnswered) {
                                          if (isCorrect) {
                                            label = "Correct";
                                            boxBg = "#22c55e";
                                            boxBorder = "#22c55e";
                                            boxColor = "#ffffff";
                                          } else {
                                            label = "Incorrect";
                                            boxBg = "#ef4444";
                                            boxBorder = "#ef4444";
                                            boxColor = "#ffffff";
                                          }
                                        }

                                        const questionNumStr = String(detailIdx + 1).padStart(2, '0');

                                        badges.push(
                                          <div
                                            key={`detail-${detailIdx}`}
                                            onClick={() => handleCellClick(att, matchingQ, label, userOpt, isCorrect)}
                                            title={`Q${detailIdx + 1}: ${matchingQ.question} (${label})`}
                                            style={{
                                              cursor: "pointer",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              width: "24px",
                                              height: "24px",
                                              borderRadius: "4px",
                                              border: `1px solid ${boxBorder}`,
                                              background: boxBg,
                                              fontSize: "11px",
                                              fontWeight: "bold",
                                              color: boxColor,
                                              userSelect: "none"
                                            }}
                                          >
                                            {questionNumStr}
                                          </div>
                                        );
                                      });
                                    } else if (Array.isArray(att.questionResults) && att.questionResults.length > 0) {
                                      att.questionResults.forEach((isCorrect, qIndex) => {
                                        const qFromFetched = resultsQuestions?.[qIndex] || test.questions?.[qIndex];

                                        let options = {};
                                        if (qFromFetched && qFromFetched.options) {
                                          if (typeof qFromFetched.options === "object" && !Array.isArray(qFromFetched.options)) {
                                            Object.entries(qFromFetched.options).forEach(([k, v]) => {
                                              options[k.toUpperCase()] = v;
                                            });
                                          } else if (Array.isArray(qFromFetched.options)) {
                                            const keys = ["A", "B", "C", "D"];
                                            qFromFetched.options.forEach((val, idx) => {
                                              options[keys[idx]] = val;
                                            });
                                          }
                                        }

                                        const matchingQ = {
                                          question: qFromFetched?.question || `Question ${qIndex + 1}`,
                                          code: qFromFetched?.code || "",
                                          options: options,
                                          correctOption: (qFromFetched?.correctOption || "A").toUpperCase(),
                                          answerText: qFromFetched?.answerText || ""
                                        };
                                        const userOpt = att.selectedOptions?.[qIndex] || null;
                                        const isNotAnswered = !userOpt ||
                                          userOpt === "" ||
                                          userOpt === "NULL" ||
                                          userOpt === "NA" ||
                                          userOpt === "N/A" ||
                                          userOpt === "—" ||
                                          userOpt === "-" ||
                                          userOpt === "—";

                                        let label = "Not Answered";
                                        let boxBg = "#ffffff";
                                        let boxBorder = "#cbd5e1";
                                        let boxColor = "#94a3b8";

                                        if (!isNotAnswered) {
                                          if (isCorrect) {
                                            label = "Correct";
                                            boxBg = "#22c55e";
                                            boxBorder = "#22c55e";
                                            boxColor = "#ffffff";
                                          } else {
                                            label = "Incorrect";
                                            boxBg = "#ef4444";
                                            boxBorder = "#ef4444";
                                            boxColor = "#ffffff";
                                          }
                                        }

                                        const questionNumStr = String(qIndex + 1).padStart(2, '0');

                                        badges.push(
                                          <div
                                            key={`result-${qIndex}`}
                                            onClick={() => handleCellClick(att, matchingQ, label, userOpt, isCorrect)}
                                            title={`Q${qIndex + 1}: ${matchingQ.question} (${label})`}
                                            style={{
                                              cursor: "pointer",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              width: "24px",
                                              height: "24px",
                                              borderRadius: "4px",
                                              border: `1px solid ${boxBorder}`,
                                              background: boxBg,
                                              fontSize: "11px",
                                              fontWeight: "bold",
                                              color: boxColor,
                                              userSelect: "none"
                                            }}
                                          >
                                            {questionNumStr}
                                          </div>
                                        );
                                      });
                                    }

                                    return badges.length > 0 ? badges : <span style={{ color: "#94a3b8", fontSize: "13px" }}>—</span>;
                                  })()}
                                </div>
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <button
                                  onClick={() => handleLoadCandidateReview(att)}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    padding: 0,
                                    width: "32px",
                                    height: "32px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    margin: "0 auto",
                                    transition: "opacity 0.2s"
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                                  title="View Details"
                                >
                                  <img src={viewRecruiterIcon} alt="View" style={{ width: "32px", height: "32px", display: "block" }} />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <StickyHorizontalScrollbar targetRef={attemptsTableContainerRef} />
              </div>
            </div>
          );

        })()}
      </div>

      {/* Add / Edit Question Modal */}
      {
        editingQuestion && (
          <div className="flash-modal-overlay">
            <div className="flash-modal-box">
              <div className="flash-modal-header">
                <h3>{editingQuestion.id ? "Edit Question" : "Add New Question"}</h3>
                <button type="button" className="flash-modal-close-btn" onClick={() => setEditingQuestion(null)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 14px",
                    fontSize: "20px",
                    fontWeight: 600,
                    background: "rgb(241, 245, 249)",
                    border: "1px solid rgb(203, 213, 225)",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >&times;</button>
              </div>
              <form onSubmit={handleSaveQuestion}>
                <div className="flash-modal-body">
                  <div className="flash-form-group" style={{ marginBottom: "16px" }}>
                    <label>Question Text<span>*</span></label>
                    <textarea
                      rows={3}
                      value={editingQuestion.question}
                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, question: e.target.value }))}
                      placeholder="Enter question text..."
                      required
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontFamily: "inherit",
                        resize: "vertical"
                      }}
                    />
                  </div>

                  <div className="flash-form-group" style={{ marginBottom: "16px" }}>
                    <label>Code Snippet (Optional)</label>
                    <textarea
                      rows={3}
                      value={editingQuestion.code}
                      onChange={(e) => setEditingQuestion(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="Enter code snippet here (if any)..."
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontFamily: "monospace",
                        resize: "vertical"
                      }}
                    />
                  </div>

                  <div className="flash-grid-2col">
                    <div className="flash-form-group">
                      <label>Option A<span>*</span></label>
                      <input
                        type="text"
                        value={editingQuestion.options.A}
                        onChange={(e) => setEditingQuestion(prev => ({
                          ...prev,
                          options: { ...prev.options, A: e.target.value }
                        }))}
                        placeholder="Option A text..."
                        required
                      />
                    </div>
                    <div className="flash-form-group">
                      <label>Option B<span>*</span></label>
                      <input
                        type="text"
                        value={editingQuestion.options.B}
                        onChange={(e) => setEditingQuestion(prev => ({
                          ...prev,
                          options: { ...prev.options, B: e.target.value }
                        }))}
                        placeholder="Option B text..."
                        required
                      />
                    </div>
                  </div>

                  <div className="flash-grid-2col">
                    <div className="flash-form-group">
                      <label>Option C<span>*</span></label>
                      <input
                        type="text"
                        value={editingQuestion.options.C}
                        onChange={(e) => setEditingQuestion(prev => ({
                          ...prev,
                          options: { ...prev.options, C: e.target.value }
                        }))}
                        placeholder="Option C text..."
                        required
                      />
                    </div>
                    <div className="flash-form-group">
                      <label>Option D<span>*</span></label>
                      <input
                        type="text"
                        value={editingQuestion.options.D}
                        onChange={(e) => setEditingQuestion(prev => ({
                          ...prev,
                          options: { ...prev.options, D: e.target.value }
                        }))}
                        placeholder="Option D text..."
                        required
                      />
                    </div>
                  </div>

                  <div className="flash-grid-2col">
                    <div className="flash-form-group">
                      <label>Correct Option<span>*</span></label>
                      <select
                        value={editingQuestion.correctOption}
                        onChange={(e) => setEditingQuestion(prev => ({ ...prev, correctOption: e.target.value }))}
                        required
                      >
                        <option value="A">Option A</option>
                        <option value="B">Option B</option>
                        <option value="C">Option C</option>
                        <option value="D">Option D</option>
                      </select>
                    </div>
                    <div className="flash-form-group">
                      <label>Explanation / Answer Text<span>*</span></label>
                      <input
                        type="text"
                        value={editingQuestion.answerText}
                        onChange={(e) => setEditingQuestion(prev => ({ ...prev, answerText: e.target.value }))}
                        placeholder="Enter correct answer explanation..."
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="flash-modal-footer">
                  <button
                    type="button"
                    onClick={() => setEditingQuestion(null)}
                    className="btn-modal-cancel"
                    disabled={isSubmittingQuestion}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: 600,
                      background: "rgb(241, 245, 249)",
                      border: "1px solid rgb(203, 213, 225)",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-modal-save"
                    disabled={isSubmittingQuestion}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: 600,
                      background: "rgb(241, 245, 249)",
                      border: "1px solid rgb(203, 213, 225)",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  >
                    {isSubmittingQuestion ? "Saving..." : "Save Question"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Question Cell Review Modal */}
      {
        cellReviewData && (
          <div className="flash-modal-overlay" onClick={() => setCellReviewData(null)}>
            <div className="flash-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px" }}>
              <div className="flash-modal-header">
                <h3>Question Response Detail</h3>
                <button type="button" className="flash-modal-close-btn" onClick={() => setCellReviewData(null)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 14px",
                    fontSize: "20px",
                    fontWeight: 600,
                    background: "rgb(241, 245, 249)",
                    border: "1px solid rgb(203, 213, 225)",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >&times;</button>
              </div>
              <div className="flash-modal-body" style={{ textAlign: "left" }}>
                <div style={{ marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "14px", color: "#64748b" }}><strong>Candidate:</strong> {cellReviewData.candidateName}</div>
                  <div style={{ fontSize: "14px", color: "#64748b" }}><strong>Register No:</strong> {cellReviewData.candidateRegno}</div>
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <h4 style={{ fontSize: "15px", fontWeight: "600", color: "#1e293b", marginBottom: "8px", lineHeight: "1.5" }}>
                    <span dangerouslySetInnerHTML={{ __html: formatQuestionText(cellReviewData.question) }} />
                  </h4>
                  {cellReviewData.code && (
                    <pre style={{
                      background: "#1e1e1e",
                      color: "#d4d4d4",
                      padding: "12px",
                      borderRadius: "6px",
                      fontFamily: "monospace",
                      overflowX: "auto",
                      margin: "12px 0",
                      borderLeft: "4px solid #F97316"
                    }}>
                      <code>{cellReviewData.code}</code>
                    </pre>
                  )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
                  {(() => {
                    let optionsEntries = [];
                    if (cellReviewData.options && typeof cellReviewData.options === "object" && !Array.isArray(cellReviewData.options)) {
                      optionsEntries = Object.entries(cellReviewData.options).map(([key, val]) => [key.toUpperCase(), val]);
                    } else if (Array.isArray(cellReviewData.options)) {
                      const keys = ["A", "B", "C", "D"];
                      optionsEntries = cellReviewData.options.map((val, idx) => [keys[idx] || "", val]);
                    }

                    return optionsEntries.map(([optionKey, optionText]) => {
                      const isSelected = cellReviewData.userOption === optionKey;
                      const isCorrectOption = optionKey === (cellReviewData.correctOption || "A").toUpperCase();

                      let optionStyle = {
                        padding: "10px 14px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        background: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontSize: "14px"
                      };

                      let icon = null;
                      let statusText = null;

                      if (isSelected && isCorrectOption) {
                        optionStyle.border = "2px solid #10b981";
                        optionStyle.background = "#ecfdf5";
                        optionStyle.color = "#065f46";
                        optionStyle.fontWeight = "600";
                        icon = <span style={{ color: "#10b981", fontWeight: "bold" }}>✓</span>;
                        statusText = <span style={{ marginLeft: "auto", color: "#10b981", fontSize: "11px", fontWeight: "700" }}>Correct Selection</span>;
                      } else if (isSelected && !isCorrectOption) {
                        optionStyle.border = "2px solid #ef4444";
                        optionStyle.background = "#fef2f2";
                        optionStyle.color = "#991b1b";
                        optionStyle.fontWeight = "600";
                        icon = <span style={{ color: "#ef4444", fontWeight: "bold" }}>✗</span>;
                        statusText = <span style={{ marginLeft: "auto", color: "#ef4444", fontSize: "11px", fontWeight: "700" }}>Candidate's Selection</span>;
                      } else if (!isSelected && isCorrectOption) {
                        optionStyle.border = "2px solid #10b981";
                        optionStyle.background = "#ecfdf5";
                        optionStyle.color = "#065f46";
                        optionStyle.fontWeight = "600";
                        icon = <span style={{ color: "#10b981", fontWeight: "bold" }}>✓</span>;
                        statusText = <span style={{ marginLeft: "auto", color: "#10b981", fontSize: "11px", fontWeight: "700" }}>Correct Answer</span>;
                      }

                      return (
                        <div key={optionKey} style={optionStyle}>
                          {icon}
                          <span><strong>{optionKey}.</strong> {optionText}</span>
                          {statusText}
                        </div>
                      );
                    });
                  })()}
                </div>

                {cellReviewData.answerText && (
                  <div style={{
                    background: "#eff6ff",
                    borderLeft: "4px solid #3b82f6",
                    padding: "12px",
                    borderRadius: "0 6px 6px 0",
                    fontSize: "13.5px",
                    color: "#1e3a8a"
                  }}>
                    <strong>Explanation:</strong> {cellReviewData.answerText}
                  </div>
                )}
              </div>
              <div className="flash-modal-footer">
                <button type="button" className="btn-modal-cancel"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: 600,
                    background: "rgb(241, 245, 249)",
                    border: "1px solid rgb(203, 213, 225)",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => setCellReviewData(null)}>Close</button>
              </div>
            </div>
          </div>
        )
      }

      {/* Toast Notifications */}
      {
        toastMessage && (
          <div className="toast-container">
            <div className="toast show">{toastMessage}</div>
          </div>
        )
      }
    </div >
  );
};

export default FlashTest;
